import { apiClient, extractData } from './api';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AvailableAccountItem {
  type: 'facebook_page' | 'instagram_business' | 'linkedin_page';
  platformAccountId: string;
  accountName: string;
  accountHandle: string | null;
  avatarUrl: string | null;
  followerCount: number;
  linkedPageId?: string;
  linkedPageName?: string;
  alreadyConnected?: boolean;
}

export interface ConnectedAccountResult {
  id: string;
  platform: string;
  platformAccountId: string;
  accountName: string | null;
  accountHandle: string | null;
  accountType: string;
  avatarUrl: string | null;
  status: string;
  followerCount: number;
}

export interface OAuthCallbackResponse {
  status: 'connected' | 'selection_required';
  account?: ConnectedAccountResult;
  availableAccounts?: AvailableAccountItem[];
  selectionToken?: string;
  guidance?: string;
}

export interface SelectAccountsResponse {
  status: 'connected';
  accounts: ConnectedAccountResult[];
  connectedCount: number;
}

export interface SelectedAccount {
  platformAccountId: string;
  type: 'facebook_page' | 'instagram_business' | 'linkedin_page';
}

export interface BlueskyConnectInput {
  workspaceId: string;
  identifier: string;
  appPassword: string;
}

export interface BlueskyConnectResponse {
  status: 'connected';
  account: ConnectedAccountResult;
}

export interface DevtoConnectInput {
  workspaceId: string;
  apiKey: string;
}

export interface DevtoConnectResponse {
  status: 'connected';
  account: ConnectedAccountResult;
}

export interface DailyDevConnectInput {
  workspaceId: string;
  accessToken: string;
}

export interface DailyDevConnectResponse {
  status: 'connected';
  account: ConnectedAccountResult;
}

export interface MastodonConnectInput {
  workspaceId: string;
  instanceUrl: string;
  accessToken: string;
}

export interface MastodonConnectResponse {
  status: 'connected';
  account: ConnectedAccountResult;
}

export interface HashnodeConnectInput {
  workspaceId: string;
  accessToken: string;
}

export interface HashnodeConnectResponse {
  status: 'connected';
  account: ConnectedAccountResult;
}

// ─── Raw Backend Response Shape ─────────────────────────────────────────────

/**
 * The actual shape returned by the backend's OAuthCallbackResult.
 * The backend returns `connected` (not `account`) and has no `status` field.
 * We normalize this into `OAuthCallbackResponse` for the frontend hooks.
 */
interface RawOAuthCallbackData {
  connected?: {
    id: string;
    platform: string;
    platformAccountId: string;
    accountName: string;
    accountType: string;
    profileImageUrl?: string | null;
  };
  availableAccounts?: AvailableAccountItem[];
  selectionToken?: string;
  platform?: string;
}

/**
 * Normalize the raw backend response into the frontend's expected OAuthCallbackResponse shape.
 * Maps `connected` → `account` and derives the `status` field.
 */
function normalizeCallbackResponse(raw: RawOAuthCallbackData): OAuthCallbackResponse {
  if (raw.connected) {
    return {
      status: 'connected',
      account: {
        id: raw.connected.id,
        platform: raw.connected.platform,
        platformAccountId: raw.connected.platformAccountId,
        accountName: raw.connected.accountName,
        accountHandle: null,
        accountType: raw.connected.accountType,
        avatarUrl: raw.connected.profileImageUrl ?? null,
        status: 'connected',
        followerCount: 0,
      },
    };
  }

  if (raw.availableAccounts && raw.selectionToken) {
    return {
      status: 'selection_required',
      availableAccounts: raw.availableAccounts,
      selectionToken: raw.selectionToken,
    };
  }

  // Fallback — should not happen, but guard against it
  return {
    status: 'connected',
  };
}

// ─── Service Functions ──────────────────────────────────────────────────────

/**
 * Exchange the OAuth authorization code with the backend for Facebook.
 * The backend validates state, exchanges for tokens, and returns
 * either a connected account or a list for selection.
 */
export async function handleFacebookCallback(
  code: string,
  state: string
): Promise<OAuthCallbackResponse> {
  const params = new URLSearchParams({ code, state });
  const raw = await extractData(
    apiClient.get<RawOAuthCallbackData>(
      `/social-accounts/callback/facebook?${params.toString()}`
    )
  );
  return normalizeCallbackResponse(raw);
}

/**
 * Exchange the OAuth authorization code with the backend for Instagram.
 * Instagram uses Meta's OAuth flow. The backend validates state,
 * exchanges for tokens, and returns either a connected account or a list for selection.
 *
 * Uses a dedicated /callback/instagram endpoint to clearly separate
 * Instagram from Facebook callbacks, though both use Meta OAuth internally.
 */
export async function handleInstagramCallback(
  code: string,
  state: string
): Promise<OAuthCallbackResponse> {
  const params = new URLSearchParams({ code, state });
  const raw = await extractData(
    apiClient.get<RawOAuthCallbackData>(
      `/social-accounts/callback/instagram?${params.toString()}`
    )
  );
  return normalizeCallbackResponse(raw);
}

/**
 * Exchange the OAuth authorization code with the backend for X (Twitter).
 * The backend validates state, exchanges code using PKCE, fetches the
 * user profile, and returns the connected account.
 */
export async function handleXCallback(
  code: string,
  state: string
): Promise<OAuthCallbackResponse> {
  const params = new URLSearchParams({ code, state });
  const raw = await extractData(
    apiClient.get<RawOAuthCallbackData>(
      `/social-accounts/callback/x?${params.toString()}`
    )
  );
  return normalizeCallbackResponse(raw);
}

/**
 * Exchange the OAuth authorization code with the backend for LinkedIn.
 * The backend validates state, exchanges for tokens, fetches administered
 * organizations, and returns either a connected page or a list for selection.
 */
export async function handleLinkedInCallback(
  code: string,
  state: string
): Promise<OAuthCallbackResponse> {
  const params = new URLSearchParams({ code, state });
  const raw = await extractData(
    apiClient.get<RawOAuthCallbackData>(
      `/social-accounts/callback/linkedin?${params.toString()}`
    )
  );
  return normalizeCallbackResponse(raw);
}

/**
 * Exchange the OAuth authorization code with the backend for YouTube.
 * The backend validates state, exchanges for tokens via Google OAuth,
 * fetches the channel profile, and returns the connected account.
 */
export async function handleYouTubeCallback(
  code: string,
  state: string
): Promise<OAuthCallbackResponse> {
  const response = await apiClient.get('/social-accounts/callback/youtube', {
    params: { code, state },
  });
  const body = response.data as { success: boolean; data: RawOAuthCallbackData };
  return normalizeCallbackResponse(body.data);
}

/**
 * Exchange the OAuth authorization code with the backend for Reddit.
 * The backend validates state, exchanges for tokens, fetches the
 * user profile, and returns the connected account.
 */
export async function handleRedditCallback(
  code: string,
  state: string
): Promise<OAuthCallbackResponse> {
  const params = new URLSearchParams({ code, state });
  const raw = await extractData(
    apiClient.get<RawOAuthCallbackData>(
      `/social-accounts/callback/reddit?${params.toString()}`
    )
  );
  return normalizeCallbackResponse(raw);
}

/**
 * Connect a Bluesky account using handle + app password.
 * Unlike other platforms, Bluesky uses the AT Protocol with direct
 * authentication instead of OAuth redirect flow.
 */
export async function connectBluesky(
  input: BlueskyConnectInput
): Promise<BlueskyConnectResponse> {
  return extractData(
    apiClient.post<BlueskyConnectResponse>('/social-accounts/connect/bluesky', input)
  );
}

/**
 * Connect a Dev.to account using an API key.
 * Dev.to uses API key authentication from https://dev.to/settings/extensions
 * instead of OAuth redirect flow.
 */
export async function connectDevto(
  input: DevtoConnectInput
): Promise<DevtoConnectResponse> {
  return extractData(
    apiClient.post<DevtoConnectResponse>('/social-accounts/connect/devto', input)
  );
}

/**
 * Connect a daily.dev account using a personal access token.
 * daily.dev uses token-based authentication.
 * Users generate access tokens from their daily.dev account settings.
 */
export async function connectDailyDev(
  input: DailyDevConnectInput
): Promise<DailyDevConnectResponse> {
  return extractData(
    apiClient.post<DailyDevConnectResponse>('/social-accounts/connect/dailydev', input)
  );
}

/**
 * Connect a Mastodon account using instance URL + access token.
 * Mastodon is a federated platform where each instance manages its own authentication.
 * Users generate access tokens from their instance's Development settings.
 */
export async function connectMastodon(
  input: MastodonConnectInput
): Promise<MastodonConnectResponse> {
  return extractData(
    apiClient.post<MastodonConnectResponse>('/social-accounts/connect/mastodon', input)
  );
}

/**
 * Connect a Hashnode account using a personal access token.
 * Hashnode uses token-based authentication via their GraphQL API.
 * Users generate personal access tokens from https://hashnode.com/settings/developer.
 */
export async function connectHashnode(
  input: HashnodeConnectInput
): Promise<HashnodeConnectResponse> {
  return extractData(
    apiClient.post<HashnodeConnectResponse>('/social-accounts/connect/hashnode', input)
  );
}

/**
 * Complete the account selection after OAuth callback (Facebook/Instagram only).
 */
export async function selectAccounts(
  selectionToken: string,
  selectedAccounts: SelectedAccount[]
): Promise<SelectAccountsResponse> {
  // Detect if this is a LinkedIn selection
  const isLinkedIn = selectedAccounts.some((a) => a.type === 'linkedin_page');

  const endpoint = isLinkedIn
    ? '/social-accounts/callback/linkedin/select'
    : '/social-accounts/callback/facebook/select';

  return extractData(
    apiClient.post<SelectAccountsResponse>(endpoint, {
      selectionToken,
      selectedAccounts,
    })
  );
}

export const socialAccountCallbackService = {
  handleFacebookCallback,
  handleInstagramCallback,
  handleXCallback,
  handleLinkedInCallback,
  handleYouTubeCallback,
  handleRedditCallback,
  connectBluesky,
  connectDevto,
  connectDailyDev,
  connectMastodon,
  connectHashnode,
  selectAccounts,
};